import React, { useEffect, useState, useMemo } from "react";
import {
    Grid, Card, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, IconButton, MenuItem, Box, Divider, Tooltip,
    InputAdornment, Chip, Paper, CardContent, CardActions, CardHeader,
    CircularProgress
} from "@mui/material";
import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StorefrontIcon from "@mui/icons-material/Storefront";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import SearchIcon from "@mui/icons-material/Search";

import { FormControl, InputLabel, Select, FormHelperText } from '@mui/material';

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import api from "core/interceptors/api";
import Swal from "sweetalert2";

// --- A dedicated, styled component for each Plan Card ---
const PlanCard = ({ plan, handleView, handleEdit, handleDelete }) => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 0.3s, box-shadow 0.3s', '&:hover': { transform: 'translateY(-5px)', boxShadow: 8 } }}>
        <CardHeader
            action={<Chip label={plan.plan_type} color={plan.plan_type === 'vendor' ? 'success' : 'info'} size="small" sx={{ textTransform: 'capitalize', fontWeight: 'bold' }} />}
            title={<MDTypography variant="h5" fontWeight="medium" textTransform="capitalize">{plan.name}</MDTypography>}
            sx={{ pb: 0 }}
        />
        <CardContent sx={{ flexGrow: 1 }}>
            <MDBox display="flex" alignItems="baseline" my={1}>
                {/* Displaying amount without rupee symbol for generic use */}
                <MDTypography variant="h3" fontWeight="bold">{Number(plan.amount).toLocaleString("en-IN")}</MDTypography>
                <MDTypography variant="button" color="text" ml={0.5}>/ {plan.period}</MDTypography>
            </MDBox>
            <MDTypography variant="body2" color="text.secondary" sx={{ minHeight: '40px' }}>{plan.description}</MDTypography>
            <Divider sx={{ my: 2 }} />
            <MDBox display="flex" alignItems="center" mb={1}><CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} /> <MDTypography variant="body2">{plan.no_of_notifications} Notifications</MDTypography></MDBox>
            <MDBox display="flex" alignItems="center"><CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} /> <MDTypography variant="body2">{plan.no_of_images_upload} Image Uploads</MDTypography></MDBox>
        </CardContent>
        <CardActions sx={{ justifyContent: 'center', p: 2, borderTop: '1px solid #eee' }}>
            <Tooltip title="View Details"><IconButton onClick={() => handleView(plan)}><VisibilityIcon color="info" /></IconButton></Tooltip>
            <Tooltip title="Edit Plan"><IconButton onClick={() => handleEdit(plan)}><EditIcon color="success" /></IconButton></Tooltip>
            <Tooltip title="Delete Plan"><IconButton onClick={() => handleDelete(plan.id)}><DeleteIcon color="error" /></IconButton></Tooltip>
        </CardActions>
    </Card>
);


function PaymentPlansPage() {
    const [plans, setPlans] = useState([]); // Stores all fetched plans
    const [filteredPlans, setFilteredPlans] = useState([]); // Stores plans after global search
    const [searchQuery, setSearchQuery] = useState(""); // State for the global search input
    const [loading, setLoading] = useState(true); // Loading state for fetching plans

    const [formData, setFormData] = useState({});
    const [formErrors, setFormErrors] = useState({});
    const [isUpdate, setIsUpdate] = useState(false);
    const [openForm, setOpenForm] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [viewData, setViewData] = useState(null);
    const navigate = useNavigate();

    useEffect(() => { fetchPlans(); }, []);

    // Effect for global search filtering
    useEffect(() => {
        const lowerCaseQuery = searchQuery.toLowerCase();
        const newFilteredPlans = plans.filter(plan => {
            // Add null/undefined checks for plan properties
            const nameMatch = plan?.name?.toLowerCase().includes(lowerCaseQuery);
            const descriptionMatch = plan?.description?.toLowerCase().includes(lowerCaseQuery);
            const planTypeMatch = plan?.plan_type?.toLowerCase().includes(lowerCaseQuery);
            const periodMatch = plan?.period?.toLowerCase().includes(lowerCaseQuery);
            return nameMatch || descriptionMatch || planTypeMatch || periodMatch;
        });
        setFilteredPlans(newFilteredPlans);
    }, [searchQuery, plans]); // Re-run when search query or the full plans list changes

    const fetchPlans = async () => {
        setLoading(true);
        try {
            const res = await api.get("/payment_plans/get");
            // Ensure res.data is an array, and filter out any null/undefined entries
            const fetchedData = Array.isArray(res.data)
                ? res.data.filter(item => item !== null && item !== undefined)
                : [];
            setPlans(fetchedData);
        } catch (error) {
            console.error("Failed to fetch plans:", error);
            setPlans([]); // Ensure plans is an empty array on error
            Swal.fire("Error", "Failed to load payment plans. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    // --- Logic for separating plans by type for rendering ---
    const vendorPlans = useMemo(() => filteredPlans.filter(p => p && p.plan_type === 'vendor'), [filteredPlans]);
    const servicePlans = useMemo(() => filteredPlans.filter(p => p && p.plan_type === 'service'), [filteredPlans]);


    const handleOpenForm = () => { setFormData({}); setFormErrors({}); setIsUpdate(false); setOpenForm(true); };
    const handleEdit = (data) => { setFormData(data); setFormErrors({}); setIsUpdate(true); setOpenForm(true); };
    const handleView = (data) => { setViewData(data); setOpenView(true); };

    const handleDelete = async (id) => {
        const result = await Swal.fire({ title: "Are you sure?", text: "This action cannot be undone.", icon: "warning", showCancelButton: true, confirmButtonColor: "#d33", cancelButtonColor: "#3085d6", confirmButtonText: "Yes, delete it!" });
        if (result.isConfirmed) {
            try {
                await api.delete(`/payment_plans/${id}`);
                Swal.fire("Deleted!", "The plan has been deleted.", "success");
                fetchPlans(); // Re-fetch to update UI after deletion
            } catch (error) {
                console.error("Error deleting plan:", error);
                Swal.fire("Error!", error.response?.data?.message || "Could not delete the plan.", "error");
            }
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name?.trim()) errors.name = "Name is required";
        if (!formData.description?.trim()) errors.description = "Description is required";
        // Validation for "Amount" to be a valid non-negative number
        if (formData.amount == null || isNaN(formData.amount) || Number(formData.amount) < 0) errors.amount = "Enter a valid non-negative number";
        if (!formData.period?.trim()) errors.period = "Period is required";
        // Ensure notifications and images are valid non-negative numbers
        if (formData.no_of_notifications == null || isNaN(formData.no_of_notifications) || Number(formData.no_of_notifications) < 0) errors.no_of_notifications = "Enter a valid number (0 or more)";
        if (formData.no_of_images_upload == null || isNaN(formData.no_of_images_upload) || Number(formData.no_of_images_upload) < 0) errors.no_of_images_upload = "Enter a valid number (0 or more)";
        if (!formData.plan_type?.trim()) errors.plan_type = "Plan type is required";
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleFormSubmit = async () => {
        if (!validateForm()) return;
        const url = isUpdate ? `/payment_plans/${formData.id}` : "/payment_plans/";
        const method = isUpdate ? 'put' : 'post';
        try {
            const res = await api[method](url, formData);
            Swal.fire("Success", res.data.message, "success");
            setOpenForm(false);
            fetchPlans(); // Re-fetch to update UI after add/update
        } catch (error) {
            console.error("Error submitting form:", error);
            Swal.fire("Error", error.response?.data?.message || "An error occurred.", "error");
        }
    };

    const formattedDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
        } catch (e) {
            return 'Invalid Date';
        }
    };

    return (
        <DashboardLayout>
            <MDBox display="flex" justifyContent="flex-end" alignItems="center">
                <LogoutButton
                    onLogout={() => {
                        navigate("/login");
                    }}
                />
            </MDBox>
            <DashboardNavbar />
            <MDBox pt={4} pb={3}>
                {/* Header Card with Global Search and Add Button */}
                <MDBox mb={3}>
                    <Card>
                        <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                            <MDBox>
                                <MDTypography variant="h5" fontWeight="medium">Plan Management</MDTypography>
                                <MDTypography variant="button" color="text">Manage plans for Vendors and Services</MDTypography>
                            </MDBox>
                            <Box display="flex" alignItems="center" gap={2}>
                                {/* Global Search Bar */}
                                <TextField
                                    label="Search plans..."
                                    variant="outlined"
                                    size="small"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ width: '250px' }}
                                />
                                <MDButton onClick={handleOpenForm} variant="gradient" color="success" startIcon={<AddCircleOutlineIcon />}>
                                    Create Plan
                                </MDButton>
                            </Box>
                        </MDBox>
                    </Card>
                </MDBox>

                {/* Loading Indicator */}
                {loading ? (
                    <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                        <CircularProgress color="info" />
                        <MDTypography variant="h6" color="text" ml={2}>Loading Plans...</MDTypography>
                    </MDBox>
                ) : (
                    <>
                        {/* --- Section for Vendor Plans --- */}
                        <MDBox mb={4}>
                            <MDTypography variant="h5" display="flex" alignItems="center" gap={1} mb={2}>
                                <StorefrontIcon color="success" /> Vendor Plans ({vendorPlans.length})
                            </MDTypography>
                            <Grid container spacing={3}>
                                {vendorPlans.length > 0 ? (
                                    vendorPlans.map((plan) => (
                                        <Grid item xs={12} sm={6} lg={4} key={plan.id}>
                                            <PlanCard plan={plan} handleView={handleView} handleEdit={handleEdit} handleDelete={handleDelete} />
                                        </Grid>
                                    ))
                                ) : (
                                    <Grid item xs={12}>
                                        <MDTypography sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}>No vendor plans available matching your search.</MDTypography>
                                    </Grid>
                                )}
                            </Grid>
                        </MDBox>

                        <Divider sx={{ my: 2 }} />

                        {/* --- Section for Service Plans --- */}
                        <MDBox>
                            <MDTypography variant="h5" display="flex" alignItems="center" gap={1} mb={2}>
                                <MiscellaneousServicesIcon color="info" /> Service Plans ({servicePlans.length})
                            </MDTypography>
                            <Grid container spacing={3}>
                                {servicePlans.length > 0 ? (
                                    servicePlans.map((plan) => (
                                        <Grid item xs={12} sm={6} lg={4} key={plan.id}>
                                            <PlanCard plan={plan} handleView={handleView} handleEdit={handleEdit} handleDelete={handleDelete} />
                                        </Grid>
                                    ))
                                ) : (
                                    <Grid item xs={12}>
                                        <MDTypography sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}>No service plans available matching your search.</MDTypography>
                                    </Grid>
                                )}
                            </Grid>
                        </MDBox>
                    </>
                )}
            </MDBox>

            {/* --- ENHANCED & RESIZED Create/Update Form Dialog --- */}
            <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ pb: 1 }}>
                    <MDTypography variant="h5" fontWeight="medium">{isUpdate ? "Update" : "Create"} Plan</MDTypography>
                    <MDTypography variant="body2" color="text.secondary">Ensure all fields are filled correctly.</MDTypography>
                </DialogTitle>
                <DialogContent dividers> {/* Added dividers */}
                    <MDBox component="form" role="form" pt={2}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth label="Plan Name"
                                    value={formData.name || ""}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    error={!!formErrors.name} helperText={formErrors.name}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth error={!!formErrors.plan_type}>
                                    <InputLabel id="plan-type-label">Plan Type</InputLabel>
                                    <Select
                                        labelId="plan-type-label"
                                        value={formData.plan_type || ""}
                                        label="Plan Type"
                                        onChange={(e) =>
                                            setFormData({ ...formData, plan_type: e.target.value })
                                        }
                                        sx={{ height: 42 }}
                                    >
                                        <MenuItem value="vendor">Vendor</MenuItem>
                                        <MenuItem value="service">Service</MenuItem>
                                    </Select>
                                    <FormHelperText>{formErrors.plan_type}</FormHelperText>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth multiline rows={3}
                                    label="Description"
                                    value={formData.description || ""}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    error={!!formErrors.description} helperText={formErrors.description}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth type="number"
                                    label="Amount"
                                    value={formData.amount || ""}
                                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                    error={!!formErrors.amount} helperText={formErrors.amount}
                                    InputProps={{
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*'
                                    }}
                                    inputProps={{ min: 0 }} // Ensures non-negative input
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth label="Period"
                                    placeholder="e.g., 30 days, 1 year"
                                    value={formData.period || ""}
                                    onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                                    error={!!formErrors.period} helperText={formErrors.period}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth type="number"
                                    label="No. of Notifications"
                                    value={formData.no_of_notifications ?? ''}
                                    onChange={(e) => setFormData({ ...formData, no_of_notifications: e.target.value })}
                                    error={!!formErrors.no_of_notifications} helperText={formErrors.no_of_notifications}
                                    InputProps={{
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*'
                                    }}
                                    inputProps={{ min: 0 }} // Ensures non-negative input
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth type="number"
                                    label="No. of Image Uploads"
                                    value={formData.no_of_images_upload ?? ''}
                                    onChange={(e) => setFormData({ ...formData, no_of_images_upload: e.target.value })}
                                    error={!!formErrors.no_of_images_upload} helperText={formErrors.no_of_images_upload}
                                    InputProps={{
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*'
                                    }}
                                    inputProps={{ min: 0 }} // Ensures non-negative input
                                />
                            </Grid>
                        </Grid>
                    </MDBox>
                </DialogContent>
                <DialogActions sx={{ p: '16px 24px' }}>
                    <MDButton onClick={() => setOpenForm(false)} color="secondary">Cancel</MDButton>
                    <MDButton onClick={handleFormSubmit} variant="contained" color="info">{isUpdate ? "Update" : "Create Plan"}</MDButton>
                </DialogActions>
            </Dialog>

            {/* --- FULLY REDESIGNED View Dialog --- */}
            {viewData && <Dialog open={openView} onClose={() => setOpenView(false)} maxWidth="sm" fullWidth>
                <DialogContent sx={{ p: 0 }}>
                    <MDBox p={3} sx={{ background: (theme) => `linear-gradient(135deg, ${viewData.plan_type === 'vendor' ? theme.palette.success.main : theme.palette.info.main}, ${viewData.plan_type === 'vendor' ? theme.palette.success.dark : theme.palette.info.dark})`, color: 'white' }}>
                        <MDTypography variant="h4" color="white" textTransform="capitalize">{viewData.name}</MDTypography>
                        <MDTypography variant="body2" color="white" textTransform="capitalize">Plan Type: {viewData.plan_type}</MDTypography>
                    </MDBox>
                    <Box p={3}>
                        <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                            <MDTypography variant="body1">{viewData.description}</MDTypography>
                        </Paper>
                        <Divider sx={{ my: 2 }}>Pricing</Divider>
                        <MDBox display="flex" justifyContent="center" alignItems="baseline" my={2}>
                            {/* Displaying amount without rupee symbol for generic use */}
                            <MDTypography variant="h1" fontWeight="bold">{Number(viewData.amount).toLocaleString('en-IN')}</MDTypography>
                            <MDTypography variant="h6" color="text" ml={1}>/ {viewData.period}</MDTypography>
                        </MDBox>
                        <Divider sx={{ my: 2 }}>Features</Divider>
                        <MDBox mt={2}>
                            <MDBox display="flex" alignItems="center" mb={1.5}>
                                <CheckCircleIcon color={viewData.plan_type === 'vendor' ? 'success' : 'info'} sx={{ mr: 1 }} />
                                <MDTypography>{viewData.no_of_notifications} Notifications Included</MDTypography>
                            </MDBox>
                            <MDBox display="flex" alignItems="center" mb={1.5}>
                                <CheckCircleIcon color={viewData.plan_type === 'vendor' ? 'success' : 'info'} sx={{ mr: 1 }} />
                                <MDTypography>{viewData.no_of_images_upload} Image Uploads Allowed</MDTypography>
                            </MDBox>
                        </MDBox>
                        <MDTypography variant="caption" color="text" sx={{ mt: 3, display: 'block', textAlign: 'center' }}>Plan created on: {formattedDate(viewData.created_at)}</MDTypography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid #eee', p: '16px 24px' }}>
                    <MDButton onClick={() => setOpenView(false)} variant="outlined" color="secondary">Close</MDButton>
                </DialogActions>
            </Dialog>}
        </DashboardLayout>
    );
}

export default PaymentPlansPage;